import angular from 'angular';
import f from 'odata-filter-builder';
import { transformODataResponse } from 'utilities/query';

import {
  ACCOUNT_EDITED,
  COMPANY_ADDED,
  COMPANY_EDITED,
  REGULATOR_ADDED,
  REGULATOR_DELETED,
  PASSWORD_CHANGED,
  USER_ADDED,
  USER_EDITED,
  USER_DELETED,
  ACCOUNT_LOCKOUT_EDITED
} from 'common/messages';
import templateUrl from './companies.html';

export default {
  templateUrl,
  bindings: {
    companies: '<',
    regulators: '<',
    adminUsers: '<',
    companyQuery: '<',
    regulatorQuery: '<',
    adminUsersQuery: '<',
    user: '<',
    limitOptions: '<'
  },
  controller: class CompaniesComponent {
    constructor($mdDialog, $scope, $state,
      AccountService, AuthService, CompanyService,
      PeopleService, RegulatorService, ToastService) {
      'ngInject';

      Object.assign(this, {
        $mdDialog,
        $scope,
        $state,
        AccountService,
        AuthService,
        CompanyService,
        PeopleService,
        RegulatorService,
        ToastService
      });
    }

    $onInit() {
      this.selectedRegulators = [];
      this.selectedAdminUsers = [];
      this.company = -1;
      this.refreshAdminUsers(); // need to do from here to apply the 'admin users' filter to the People OData query
    }

    refreshCompanies() {
      this.promise = this.CompanyService.search(this.companyQuery);
      this.promise.then((r) => {
        this.companies = transformODataResponse(r);
      }).catch(error => this.ToastService.showError(error));
    }

    onCompanySearchTextChanged({ searchText }) {
      this.companyQuery = {
        ...this.companyQuery,
        filter: f().contains('MemberName', searchText)
      };

      this.refreshCompanies();
    }

    onCompanyQueryChanged({ query }) {
      this.companyQuery = query;
      this.refreshCompanies();
    }

    refreshRegulators() {
      this.promise = this.RegulatorService.search(this.regulatorQuery);
      this.promise.then((r) => {
        this.regulators = transformODataResponse(r);
      }).catch(error => this.ToastService.showError(error));
    }

    onRegulatorSearchTextChanged({ searchText }) {
      this.regulatorQuery = {
        ...this.regulatorQuery,
        filter: f().contains('AgentName', searchText)
      };

      this.refreshRegulators();
    }

    onRegulatorQueryChanged({ query }) {
      this.regulatorQuery = query;
      this.refreshRegulators();
    }

    showCompanyAdd() {
      const scope = angular.extend(this.$scope.$new(true), {
        onCompanyAdded: $event => this.onCompanyAdded($event)
      });

      this.$mdDialog.show({
        clickOutsideToClose: true,
        fullscreen: true,
        scope,
        template: `
          <md-dialog class="flex-50">
            <md-dialog-content>
              <dialog-close/>
              <company-add-form
                on-company-changed="onCompanyAdded($event)">
              </company-add-form>
            </md-dialog-content>
          </md-dialog>
          `
      });
    }

    onCompanyAdded({ company }) {
      if (company) {
        this.CompanyService.add(company)
          .then((r) => {
            this.$mdDialog.hide();
            this.ToastService.showSuccess(COMPANY_ADDED);

            const companyId = r.data.Id;
            this.$state.go('company', { id: companyId });
          }).catch(error => this.ToastService.showError(error));
      }
    }

    showRegulatorAdd() {
      const scope = angular.extend(this.$scope.$new(true), {
        onRegulatorAdded: $event => this.onRegulatorAdded($event)
      });

      this.$mdDialog.show({
        clickOutsideToClose: true,
        fullscreen: true,
        scope,
        template: `
          <md-dialog class="flex-50">
            <md-dialog-content>
              <dialog-close/>
              <regulator-add-form
                dialog-title="Add New Regulator"
                submit-title="Add Regulator"
                on-regulator-changed="onRegulatorAdded($event)">
              </regulator-add-form>
            </md-dialog-content>
          </md-dialog>
          `
      });
    }

    onRegulatorAdded({ regulator }) {
      if (regulator) {
        this.RegulatorService.add(regulator)
          .then(() => {
            this.ToastService.showSuccess(REGULATOR_ADDED);
            this.$mdDialog.hide();
            this.refreshRegulators();
          }).catch(error => this.ToastService.showError(error));
      }
    }

    showChangeOwnPassword() {
      const scope = angular.extend(this.$scope.$new(true), {
        ownPasswordChanged: $event => this.onOwnPasswordChanged($event)
      });

      this.$mdDialog.show({
        clickOutsideToClose: true,
        fullscreen: true,
        scope,
        template: `
          <md-dialog class="flex-50">
            <md-dialog-content>
              <dialog-close/>
              <change-password password-changed="ownPasswordChanged($event)">
              </change-password>
            </md-dialog-content>
          </md-dialog>
          `
      });
    }

    onOwnPasswordChanged({ credentials }) {
      if (credentials && this.user.Username) {
        this.AccountService.changePassword({
          Username: this.user.Username,
          ...credentials
        }).then(() => {
          this.$mdDialog.hide();
          this.ToastService.showSuccess(PASSWORD_CHANGED);
        }).catch(error => this.ToastService.showError(error));
      }
    }

    updateAccount({ user }) {
      if (user) {
        this.AccountService.updateAccount(user)
          .then(() => {
            this.ToastService.showSuccess(ACCOUNT_EDITED);
            this.$state.reload();
          }).catch(error => this.ToastService.showError(error));
      }
    }

    updateCompliance({ company }) {
      this.CompanyService.updateCompliance(company)
        .then(() => {
          this.ToastService.showSuccess(COMPANY_EDITED);
        }).catch(error => this.ToastService.showError(error));
    }

    showUserAdd() {
      const scope = angular.extend(this.$scope.$new(true), {
        accessLevels: this.AuthService.getAccessLevels(),
        onUserAdded: $event => this.onUserAdded($event)
      });

      this.$mdDialog.show({
        clickOutsideToClose: true,
        fullscreen: true,
        scope,
        template: `
        <md-dialog class="flex-50">
          <md-dialog-content>
            <dialog-close/>
            <user-add-form
              require-user-type-selection="true"
              access-levels="accessLevels"
              on-user-changed="onUserAdded($event)">
            </user-add-form>
          </md-dialog-content>
        </md-dialog>
        `
      });
    }

    onUserAdded({ user }) {
      if (user) {
        this.AccountService.addAccount({
          ...user
        }).then(() => {
          this.$mdDialog.hide();
          this.ToastService.showSuccess(USER_ADDED);
        }).catch(error => this.ToastService.showError(error));
      }
    }

    regulatorDelete(regulators) {
      const confirm = this.$mdDialog.confirm()
        .title('Delete selected regulators?')
        .textContent('Once deleted, the selected regulator(s) cannot be recovered!')
        .ok('Delete')
        .cancel('Cancel');

      this.$mdDialog.show(confirm).then(() => {
        const promises = [];
        for (let i = regulators.length - 1; i >= 0; i -= 1) {
          promises.push(this.RegulatorService.delete(regulators[i].RegulatorId));
        }

        Promise.all(promises).then(() => {
          this.ToastService.showSuccess(REGULATOR_DELETED);
          this.selectedRegulators = [];
          this.refreshRegulators();
        }).catch(error => this.ToastService.showError(error));
      });
    }

    refreshAdminUsers() {
      this.selectedAdminUsers = [];
      let filter = f();

      if (this.searchText && this.searchText.length) {
        filter = filter.contains('LastName', this.searchText)
          .or(x => x.contains('FirstName', this.searchText));
      }

      filter = filter.and(x => x.eq('UserTypeName', 'System Administrator'))
        .and(x => x.ne('UserId', this.user.UserId))
        .and(x => x.eq('IsSysAdminOwner', false))
        .and(x => x.eq('TypeReferenceId', -1));

      this.promise = this.PeopleService.search({
        ...this.adminUsersQuery,
        filter
      });

      this.promise.then((r) => {
        this.adminUsers = transformODataResponse(r);
      }).catch(error => this.ToastService.showError(error));
    }

    onAdminSearchTextChanged({ searchText }) {
      this.searchText = searchText;
      this.refreshAdminUsers();
    }

    onAdminQueryChanged({ query }) {
      this.query = query;
      this.refreshAdminUsers();
    }

    adminUserAdd() {
      const scope = angular.extend(this.$scope.$new(true), {
        onAdminUserAdded: $event => this.onAdminUserAdded($event)
      });

      this.$mdDialog.show({
        clickOutsideToClose: true,
        fullscreen: true,
        scope,
        template: `
        <md-dialog class="flex-50">
          <md-dialog-content>
            <dialog-close/>
            <admin-user-add-form
              on-user-changed="onAdminUserAdded($event)">
            </admin-user-add-form>
          </md-dialog-content>
        </md-dialog>
        `
      });
    }

    onAdminUserAdded({ user }) {
      if (user) {
        this.AccountService.addAccount({
          ...user
        }).then(() => {
          this.$mdDialog.hide();
          this.ToastService.showSuccess(USER_ADDED);
          this.refreshAdminUsers();
        }).catch(error => this.ToastService.showError(error));
      }
    }

    adminUserEdit(user) {
      const scope = angular.extend(this.$scope.$new(true), {
        user: angular.copy(user),
        onAdminUserEdited: $event => this.onAdminUserEdited($event)
      });

      this.$mdDialog.show({
        clickOutsideToClose: true,
        fullscreen: true,
        scope,
        template: `
          <md-dialog class="flex-50">
            <md-dialog-content>
              <dialog-close/>
              <admin-user-edit-form
                user="user"
                on-user-changed="onAdminUserEdited($event)">
              </admin-user-edit-form>
            </md-dialog-content>
          </md-dialog>
          `
      });
    }

    onAdminUserEdited({ user }) {
      if (user) {
        this.AccountService.updateAccount(user)
          .then(() => {
            this.ToastService.showSuccess(USER_EDITED);
            this.$mdDialog.hide();
            this.refreshAdminUsers();
          }).catch(error => this.ToastService.showError(error));
      }
    }

    adminUserDelete(users) {
      const confirm = this.$mdDialog.confirm()
        .title('Delete selected users?')
        .textContent('Once deleted, the selected user(s) cannot be recovered!')
        .ok('Delete')
        .cancel('Cancel');

      this.$mdDialog.show(confirm).then(() => {
        const promises = [];
        for (let i = users.length - 1; i >= 0; i -= 1) {
          promises.push(this.AccountService.deleteAccount(users[i].UserId));
        }

        Promise.all(promises).then(() => {
          this.ToastService.showSuccess(USER_DELETED);
          this.refreshAdminUsers();
        }).catch(error => this.ToastService.showError(error));
      });
    }

    showChangeAdminPassword(user) {
      const scope = angular.extend(this.$scope.$new(true), {
        passwordChanged: $event => this.onAdminPasswordChanged($event),
        credentials: {
          UserId: user.UserId
        }
      });

      this.$mdDialog.show({
        clickOutsideToClose: true,
        fullscreen: true,
        scope,
        template: `
          <md-dialog class="flex-50">
            <md-dialog-content>
              <dialog-close/>
              <change-password password-changed="passwordChanged($event)"
                require-current-password="false"
                credentials="credentials">
              </change-password>
            </md-dialog-content>
          </md-dialog>
          `
      });
    }

    onAdminPasswordChanged({ credentials }) {
      if (credentials) {
        this.AccountService.forcePasswordChange({
          ...credentials
        }).then(() => {
          this.$mdDialog.hide();
          this.ToastService.showSuccess(PASSWORD_CHANGED);
          this.selectedAdminUsers = [];
        }).catch(error => this.ToastService.showError(error));
      }
    }

    setUserLockout({ userId, isLocked }) {
      this.AccountService.setLockoutStatus(userId, isLocked)
        .then(() => {
          this.ToastService.showSuccess(ACCOUNT_LOCKOUT_EDITED);
        }).catch(error => this.ToastService.showError(error));
    }
  }
};



// WEBPACK FOOTER //
// ./components/app/admin/companies/companies.component.js